import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistory } from 'react-router'
import { Text, Button } from '@conexasaude/conexa-hero'
import { useModalContext } from '@/components/RebrandedModal/contexts/ModalContext'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { sendMetrics } from '@/modules/RealtimeAttendance/services'
import { useStyles } from './styles'
import { ATTENDANCE_STATES } from '@/modules/RealtimeAttendance/constants'
import CustomLayout from '@/components/RebrandedCustomLayout'
import InviteToCallModal from '../../components/InviteToCallModal'
import { RenderIf } from '@/components/RenderIf'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { MeetingType } from './types'
import { mergeParams } from '@/utils/mergeParams'
import { getEnableTranscription } from '../../video-call-services'
import { CanceledByVideoErrorModal } from '@/modules/RealtimeAttendance/components/CanceledByVideoErrorModal'

type MeetParams = {
  skipGuide?: string
  flagTranscription?: string
}

const VideoRoom: FunctionComponent = () => {
  const history = useHistory()
  const [sessionId, setSessionId] = useState('')
  const { realtimeAttendance, callingTimes, clearTimer } =
    useRealtimeAttendanceContext()
  const { clinic } = useAuthContext()
  const { showModal } = useModalContext()
  const refMeeting = useRef<HTMLIFrameElement>(null)
  const styles = useStyles()
  const [meetingUrl, setMeetingUrl] = useState('')

  const isAnymeet =
    realtimeAttendance?.call?.meetingType === MeetingType.ANYMEET

  const unblock = history.block(() => {
    if (realtimeAttendance?.call?.queueStatus) {
      switch (realtimeAttendance?.call?.queueStatus) {
        case ATTENDANCE_STATES.CALL_STARTED:
        case ATTENDANCE_STATES.CALL_RESTARTED:
          return false
      }
    }
  })

  const handleInviteClick = () => {
    showModal('INVITE_TO_CALL_MODAL', {
      title: 'Convide alguém para a chamada',
    })
  }

  useEffect(() => {
    if (!realtimeAttendance?.call) return

    const sendMetricsAndClearTimer = async () => {
      await sendMetrics(realtimeAttendance?.call?.callId, {
        callingTimes,
        status: realtimeAttendance?.call?.queueStatus,
      })

      clearTimer()
    }
    sendMetricsAndClearTimer()

    if (realtimeAttendance?.call?.zoomMeetingUrl?.includes('express')) {
      setSessionId(realtimeAttendance?.call?.zoomMeetingId)
      return
    }

    const urlParams = new URLSearchParams(
      realtimeAttendance?.call?.zoomMeetingUrl,
    )
    setSessionId(urlParams.get('sessionId'))
    // eslint-disable-next-line
  }, [realtimeAttendance?.call])

  useEffect(() => {
    return () => {
      unblock()
    }
  }, [unblock])

  const canInvitePeople = useMemo(() => {
    return (
      clinic?.flag?.PermiteCriacaoUrlConvidado &&
      realtimeAttendance?.call?.meetingType &&
      !isAnymeet
    )
  }, [
    clinic?.flag?.PermiteCriacaoUrlConvidado,
    isAnymeet,
    realtimeAttendance?.call?.meetingType,
  ])

  const generateMeetingUrl = useCallback(async () => {
    const enableTranscription = await getEnableTranscription(
      realtimeAttendance?.call?.attendanceId,
    )

    const params: MeetParams = {}

    if (isAnymeet) {
      params.skipGuide = 'true'
    }

    if (enableTranscription) {
      params.flagTranscription = 'true'
    }

    const mergetMeetingUrl = mergeParams(
      realtimeAttendance?.call?.zoomMeetingUrl,
      params,
    )

    setMeetingUrl(mergetMeetingUrl)
  }, [
    isAnymeet,
    realtimeAttendance?.call?.attendanceId,
    realtimeAttendance?.call?.zoomMeetingUrl,
  ])

  useEffect(() => {
    generateMeetingUrl()
  }, [generateMeetingUrl])

  useEffect(() => {
    if (meetingUrl) {
      const handleClosedApplication = () => {
        const meetFrame = refMeeting.current

        meetFrame?.contentWindow?.postMessage('CLOSED_APPLICATION', '*')
      }

      window.addEventListener('beforeunload', handleClosedApplication)

      return () => {
        window.removeEventListener('beforeunload', handleClosedApplication)
      }
    }
  }, [meetingUrl])

  return (
    <CustomLayout>
      <div css={styles.container}>
        <div>
          <Text variant="headingLarge">
            Videochamada com {realtimeAttendance?.call?.professionalName}
          </Text>

          <ul css={styles.meetingInformations}>
            <li>
              <Text>
                Tipo de consulta:{' '}
                <span>
                  {realtimeAttendance?.isImmediate ? 'Imediata' : 'Agendada'}
                </span>
              </Text>
            </li>
            <li>
              <Text>
                Especialidade:{' '}
                <span>{realtimeAttendance?.call?.speciality}</span>
              </Text>
            </li>
          </ul>

          <RenderIf condition={canInvitePeople}>
            <Button css={styles.inviteGuestButton} onClick={handleInviteClick}>
              Convidar pessoas
            </Button>
          </RenderIf>
        </div>

        <RenderIf condition={!!realtimeAttendance?.call?.zoomMeetingUrl}>
          <iframe
            src={meetingUrl}
            width="100%"
            height="550"
            name="meetConference"
            allow="camera; microphone; display-capture;"
            allowFullScreen
            ref={refMeeting}
          />
        </RenderIf>
      </div>

      <RenderIf condition={clinic?.flag?.PermiteCriacaoUrlConvidado}>
        <InviteToCallModal sessionId={sessionId} />
      </RenderIf>

      <CanceledByVideoErrorModal />
    </CustomLayout>
  )
}

export default VideoRoom
